import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Skeleton } from '@material-ui/lab';
import { Box, makeStyles, Typography } from '@popmenu/common-ui';
import type { UserTasteComparisonDishFragment } from '~/libs/gql/fragments/eat/UserTasteComparisonFragment.generated';
import CustomImg from '~/shared/CustomImg';

const useStyles = makeStyles(theme => ({
  card: {
    '&:disabled': {
      cursor: 'default',
      opacity: 0.6,
    },
    '&:hover, &:focus-visible': {
      borderColor: theme.palette.primary.main,
    },
    backgroundColor: theme.palette.common.white,
    border: `2px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(2),
    color: theme.palette.text.primary,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    font: 'inherit',
    overflow: 'hidden',
    padding: 0,
    textAlign: 'left',
    width: '100%',
  },
  // Not clickable, so no pointer or hover border
  cardPlaceholder: {
    '&:hover, &:focus-visible': {
      borderColor: theme.palette.grey[300],
    },
    cursor: 'default',
  },
  description: {
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2.5),
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(0.25),
      padding: theme.spacing(1.5, 1.75),
    },
  },
  name: {
    fontSize: '1.375rem',
    fontWeight: 600,
    [theme.breakpoints.down('xs')]: {
      // Two lines at most, so a long name can't squeeze the photo
      display: '-webkit-box',
      fontSize: '1.125rem',
      overflow: 'hidden',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
    },
  },
  photo: {
    height: '100%',
    left: 0,
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  photoContainer: {
    backgroundColor: theme.palette.gray.main,
    paddingTop: '75%',
    position: 'relative',
    [theme.breakpoints.down('xs')]: {
      // Takes whatever height the card has left after its text, but stays tall enough to judge the dish. On short
      // screens the page scrolls instead of squeezing the photo.
      flex: '1 1 0',
      minHeight: theme.spacing(20),
      paddingTop: 0,
    },
  },
  restaurantName: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
}));

type RefineDishCardProps = {
  disabled: boolean
  dish: UserTasteComparisonDishFragment
  onPick: () => void
}

// One side of "Which dish would you rather try?". Shows the dish as a diner would see it, never our attribute tags.
export const RefineDishCard = ({ disabled, dish, onPick }: RefineDishCardProps) => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <button
      aria-label={intl.formatMessage({ defaultMessage: 'I would rather try {name}' }, { name: dish.displayName })}
      className={classes.card}
      disabled={disabled}
      onClick={onPick}
      type="button"
    >
      <Box className={classes.photoContainer}>
        {dish.featuredPhoto?.photoUrl && (
          <CustomImg
            alt=""
            className={classes.photo}
            src={dish.featuredPhoto.photoUrl}
          />
        )}
      </Box>
      <Box className={classes.details}>
        <Typography className={classes.name} component="span">
          {dish.displayName}
        </Typography>
        {dish.restaurant && dish.location && (
          <Typography className={classes.restaurantName} component="span">
            <FormattedMessage
              defaultMessage="{restaurantName} in {city}, {stateOrCountry}"
              values={{
                city: dish.location.city,
                restaurantName: dish.restaurant.name,
                stateOrCountry: dish.location.state || dish.location.countryName,
              }}
            />
          </Typography>
        )}
        {dish.description && (
          <Typography className={classes.description} component="span">
            {dish.description}
          </Typography>
        )}
      </Box>
    </button>
  );
};

// Stands in for a card while the next pair loads, with the same border, photo shape and text lines so nothing shifts
export const RefineDishCardSkeleton = () => {
  const classes = useStyles();

  return (
    <Box aria-hidden className={`${classes.card} ${classes.cardPlaceholder}`} component="span">
      <Box className={classes.photoContainer}>
        <Skeleton className={classes.photo} variant="rect" />
      </Box>
      <Box className={classes.details}>
        <Skeleton className={classes.name} variant="text" width="70%" />
        <Skeleton className={classes.restaurantName} variant="text" width="50%" />
        <Skeleton className={classes.description} variant="text" width="90%" />
      </Box>
    </Box>
  );
};
